import {
  GridRowId,
  GridColDef,
  GridRowModel,
  GridRowModes,
  GridRowsProp,
  GridEventListener,
  GridRowModesModel,
  GridValidRowModel,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from '@mui/x-data-grid-premium';
import { useIntl } from 'react-intl';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Dispatch, SetStateAction } from 'react';
import { renderDataGridLocaleText } from 'utils';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { EditToolbarRelatedContacts, RenderEditItem, StyledBox, ZebraDataGrid } from 'components';

type RelatedContactsCreateTableProps = {
  rows: GridRowsProp;
  setRows: Dispatch<SetStateAction<readonly GridValidRowModel[]>>;
  rowModesModel: GridRowModesModel;
  setRowModesModel: Dispatch<SetStateAction<GridRowModesModel>>;
};

const RelatedContactsCreateTable = ({
  rows,
  setRows,
  rowModesModel,
  setRowModesModel,
}: RelatedContactsCreateTableProps) => {
  const intl = useIntl();

  // dynamic variables
  const columns: GridColDef[] = [
    {
      field: 'order',
      headerName: intl.formatMessage({ id: 'project.table.order', defaultMessage: 'Sıra No' }),
      width: 72,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      editable: false,
    },
    {
      field: 'nameSurname',
      headerName: intl.formatMessage({ id: 'project.table.nameSurname', defaultMessage: 'Ad Soyad' }),
      flex: 1,
      minWidth: 180,
      editable: true,
      preProcessEditCellProps: (params) => {
        const isEmpty = !params.props.value;

        const errorMessage = isEmpty
          ? intl.formatMessage({
              id: 'project.requiredFieldsOfRelatedContacts',
              defaultMessage: 'İlgili Kişilerin bilgileri boş bırakılamaz!',
            })
          : false;

        return { ...params.props, error: errorMessage };
      },
      renderEditCell: RenderEditItem,
    },
    {
      field: 'email',
      headerName: intl.formatMessage({ id: 'project.table.email', defaultMessage: 'E-Posta' }),
      flex: 1,
      minWidth: 220,
      editable: true,
      preProcessEditCellProps: (params) => {
        const value: string = params.props.value || '';
        const isEmpty = !value;
        const hasSurroundingWhitespace = value !== value.trim();
        const hasUpperCase = /[A-Z]/.test(value);
        const hasTurkishChars = /[çÇğĞıİöÖşŞüÜ]/.test(value);
        const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
        const isInvalidEmail = !isEmpty && !emailRegex.test(value);

        let errorMessage: string | false = false;

        if (isEmpty) {
          errorMessage = intl.formatMessage({
            id: 'project.requiredFieldsOfRelatedContacts',
            defaultMessage: 'İlgili Kişilerin bilgileri boş bırakılamaz!',
          });
        } else if (hasSurroundingWhitespace) {
          errorMessage = intl.formatMessage({
            id: 'validations.emailNoWhitespace',
            defaultMessage: 'E-posta adresinin başında veya sonunda boşluk olamaz!',
          });
        } else if (hasUpperCase) {
          errorMessage = intl.formatMessage({
            id: 'validations.emailNoUpperCase',
            defaultMessage: 'E-posta adresi büyük harf içeremez!',
          });
        } else if (hasTurkishChars) {
          errorMessage = intl.formatMessage({
            id: 'validations.emailNoTurkishChars',
            defaultMessage: 'E-posta adresi Türkçe karakter içeremez!',
          });
        } else if (isInvalidEmail) {
          errorMessage = intl.formatMessage({
            id: 'validations.invalidEmailFormat',
            defaultMessage: 'Geçersiz e-posta formatı',
          });
        }

        return { ...params.props, error: errorMessage };
      },
      renderEditCell: RenderEditItem,
    },
    {
      field: 'phoneNumber',
      headerName: intl.formatMessage({ id: 'project.table.phoneNumber', defaultMessage: 'Telefon' }),
      flex: 1,
      minWidth: 160,
      editable: true,
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: intl.formatMessage({ id: 'project.table.actions', defaultMessage: 'İşlemler' }),
      width: 100,
      cellClassName: 'actions',
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label={intl.formatMessage({ id: 'project.table.save', defaultMessage: 'Kaydet' })}
              sx={{
                color: 'primary.main',
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label={intl.formatMessage({ id: 'project.table.cancel', defaultMessage: 'Vazgeç' })}
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label={intl.formatMessage({ id: 'project.table.edit', defaultMessage: 'Düzenle' })}
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label={intl.formatMessage({ id: 'project.table.delete', defaultMessage: 'Sil' })}
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];

  // sync helper functions
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow!.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false };
    setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  return (
    <StyledBox sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflow: 'hidden' }}>
      <ZebraDataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        localeText={renderDataGridLocaleText(intl)}
        initialState={{ pinnedColumns: { right: ['actions'] } }}
        slots={{ toolbar: () => <EditToolbarRelatedContacts setRowModesModel={setRowModesModel} setRows={setRows} /> }}
        slotProps={{ toolbar: { setRows, setRowModesModel } }}
        hideFooter
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnSorting
        sx={{ border: '1px solid', borderColor: 'divider' }}
        disableAggregation
        disableRowGrouping
      />
    </StyledBox>
  );
};

export default RelatedContactsCreateTable;
